"use client";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@workspace/ui/components/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@workspace/ui/components/popover";
import { ScrollArea } from "@workspace/ui/components/scroll-area";
import { cn } from "@workspace/ui/lib/utils";
import { countries } from "country-data-list";
import { OTPInput, OTPInputContext } from "input-otp";
import {
  type CountryCode,
  getCountryCallingCode,
  isValidPhoneNumber,
} from "libphonenumber-js";
import { CheckIcon } from "lucide-react";
import { motion } from "motion/react";
import * as React from "react";
import { CircleFlag } from "react-circle-flags";

// ============================================================================
// InputOTP Components
// ============================================================================

function InputOTP({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<typeof OTPInput> & {
  containerClassName?: string;
}) {
  return (
    <OTPInput
      className={cn("disabled:cursor-not-allowed", className)}
      containerClassName={cn(
        "flex items-center gap-2 has-disabled:opacity-50",
        containerClassName
      )}
      data-slot="input-otp"
      {...props}
    />
  );
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("flex items-center gap-1", className)}
      data-slot="input-otp-group"
      {...props}
    />
  );
}

// Glass effect styles
const glassStyles = {
  blur: "backdrop-blur-md",
};

function InputOTPSlot({
  index,
  className,
  glassEffect = false,
  size = "default",
  ...props
}: React.ComponentProps<"div"> & {
  index: number;
  glassEffect?: boolean;
  size?: "default" | "sm" | "xs";
}) {
  const inputOTPContext = React.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};

  const sizeClasses = {
    default: "h-9 w-9 text-sm",
    sm: "h-[31px] w-[31px] text-sm",
    xs: "h-[26px] w-[26px] text-[10px] rounded-[4px]",
  }[size];

  return (
    <div
      className={cn(
        // Base styles - individual squircles (not connected)
        "relative flex items-center justify-center rounded-lg border transition-all outline-none",
        sizeClasses,
        // Active state
        "data-[active=true]:border-ring data-[active=true]:ring-ring/50 data-[active=true]:z-10 data-[active=true]:ring-[3px]",
        // Invalid states
        "data-[active=true]:aria-invalid:ring-destructive/20 dark:data-[active=true]:aria-invalid:ring-destructive/40 aria-invalid:border-destructive data-[active=true]:aria-invalid:border-destructive",
        // Glass effect styles
        glassEffect && [
          glassStyles.blur,
          "bg-white/70 dark:bg-white/10 border-black/8 dark:border-white/15 shadow-sm dark:shadow-none",
        ],
        // Default styles (non-glass)
        !glassEffect &&
          "border-input bg-background/50 dark:bg-input/30 shadow-xs",
        className
      )}
      data-active={isActive}
      data-glass={glassEffect}
      data-slot="input-otp-slot"
      style={
        glassEffect
          ? {
              boxShadow: isActive
                ? undefined
                : "var(--glass-inner-shadow), var(--glass-glow)",
            }
          : undefined
      }
      {...props}
    >
      {char && <span className="phone-digit">{char}</span>}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="animate-caret-blink bg-foreground h-[60%] w-px duration-1000" />
        </div>
      )}
    </div>
  );
}

function InputOTPSeparator({
  className,
  ...props
}: React.ComponentProps<"div">) {
  // Generate stable random values on mount for organic feel
  const randomOffset = React.useMemo(() => Math.random() * 2, []);
  const randomDuration = React.useMemo(() => 1.8 + Math.random() * 0.8, []);
  const randomAmplitude = React.useMemo(() => 2.5 + Math.random() * 1.5, []);

  // Traveling wave keyframes - wave moves left to right
  const waveKeyframes = React.useMemo(() => {
    const a = randomAmplitude;
    return [
      // Wave traveling: peak moves from left to center to right
      `M -2 8 C 2 ${8 - a}, 6 ${8 + a}, 10 8 S 18 ${8 - a}, 26 8`,
      `M -2 8 C 2 ${8 + a * 0.5}, 6 ${8 - a}, 10 ${8 + a * 0.7} S 18 ${8 - a * 0.5}, 26 8`,
      `M -2 8 C 2 8, 6 ${8 + a * 0.5}, 10 ${8 - a} S 18 ${8 + a}, 26 8`,
      `M -2 8 C 2 ${8 - a * 0.5}, 6 8, 10 ${8 + a * 0.5} S 18 ${8 - a}, 26 8`,
      `M -2 8 C 2 ${8 - a}, 6 ${8 + a}, 10 8 S 18 ${8 - a}, 26 8`,
    ];
  }, [randomAmplitude]);

  return (
    <div
      className={cn("flex items-center overflow-visible", className)}
      data-slot="input-otp-separator"
      role="separator"
      style={{ margin: "5px 0px" }}
      {...props}
    >
      <motion.svg
        animate={{
          opacity: [0.35, 0.55, 0.35],
        }}
        className="text-muted-foreground overflow-visible"
        height="16"
        style={{ overflow: "visible" }}
        transition={{
          duration: randomDuration * 1.2,
          delay: randomOffset * 0.3,
          ease: "easeInOut",
          repeat: Number.POSITIVE_INFINITY,
        }}
        viewBox="0 0 24 16"
        width="12"
      >
        <motion.path
          animate={{
            d: waveKeyframes,
          }}
          d={waveKeyframes[0]}
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.5"
          transition={{
            d: {
              duration: randomDuration,
              ease: "easeInOut",
              repeat: Number.POSITIVE_INFINITY,
              delay: randomOffset * 0.2,
            },
          }}
        />
      </motion.svg>
    </div>
  );
}

// ============================================================================
// Phone Input Components
// ============================================================================

// Country data type
interface CountryData {
  code: CountryCode;
  name: string;
  callingCode: string;
}

// Get supported countries with their calling codes
function getSupportedCountries(): CountryData[] {
  const supportedCountries: CountryData[] = [];

  for (const country of countries.all) {
    if (
      country.alpha2 &&
      country.name &&
      country.countryCallingCodes?.length > 0
    ) {
      try {
        const code = country.alpha2 as CountryCode;
        const callingCode = getCountryCallingCode(code);
        supportedCountries.push({
          code,
          name: country.name,
          callingCode: String(callingCode),
        });
      } catch {
        // Skip countries not supported by libphonenumber-js
      }
    }
  }

  return supportedCountries.sort((a, b) => a.name.localeCompare(b.name));
}

const SUPPORTED_COUNTRIES = getSupportedCountries();

// Phone number format patterns by country
// These define how digits are grouped (e.g., US: 3-3-4)
const PHONE_FORMATS: Record<string, number[]> = {
  US: [3, 3, 4], // (XXX) XXX-XXXX
  CA: [3, 3, 4], // Same as US
  GB: [4, 3, 3], // XXXX XXX XXX
  AU: [4, 3, 3], // XXXX XXX XXX
  DE: [4, 4, 4], // XXXX XXXX XXXX (simplified)
  FR: [2, 2, 2, 2, 2], // XX XX XX XX XX
  JP: [3, 4, 4], // XXX-XXXX-XXXX
  CN: [3, 4, 4], // XXX XXXX XXXX
  IN: [5, 5], // XXXXX XXXXX
  BR: [2, 5, 4], // XX XXXXX-XXXX
  MX: [3, 3, 4], // XXX XXX XXXX
  KR: [3, 4, 4], // XXX-XXXX-XXXX
  IT: [3, 3, 4], // XXX XXX XXXX
  ES: [3, 3, 3], // XXX XXX XXX
  RU: [3, 3, 2, 2], // XXX XXX-XX-XX
  default: [3, 3, 4], // Default to US format
};

function getPhoneFormat(countryCode: CountryCode): number[] {
  return PHONE_FORMATS[countryCode] ?? PHONE_FORMATS.default!;
}

function getTotalDigits(format: number[]): number {
  return format.reduce((sum, n) => sum + n, 0);
}

// CountrySelect Component
interface CountrySelectProps {
  value: CountryCode;
  onChange: (country: CountryCode) => void;
  disabled?: boolean;
  className?: string;
  size?: number;
}

function CountrySelect({
  value,
  onChange,
  disabled = false,
  className,
  size = 28,
}: CountrySelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  return (
    <Popover
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (isOpen) setSearch("");
      }}
      open={open}
    >
      <PopoverTrigger asChild>
        <button
          aria-label="Select country"
          className={cn(
            // Just the circular flag, no container
            "relative flex items-center justify-center transition-all outline-none",
            "focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:rounded-full",
            "disabled:pointer-events-none disabled:opacity-50",
            className
          )}
          disabled={disabled}
          type="button"
        >
          <CircleFlag
            countryCode={value.toLowerCase()}
            height={size}
            width={size}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-70 p-0">
        <Command>
          <CommandInput
            onValueChange={setSearch}
            placeholder="Search country..."
            value={search}
          />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <ScrollArea className="h-64">
              <CommandGroup>
                {SUPPORTED_COUNTRIES.map((country) => (
                  <CommandItem
                    className="gap-2"
                    key={country.code}
                    onSelect={() => {
                      onChange(country.code);
                      setOpen(false);
                    }}
                    value={`${country.name} ${country.code}`}
                  >
                    <CircleFlag
                      countryCode={country.code.toLowerCase()}
                      height={16}
                      width={16}
                    />
                    <span className="flex-1 text-sm">{country.name}</span>
                    <span className="text-muted-foreground text-xs">
                      +{country.callingCode}
                    </span>
                    <CheckIcon
                      className={cn(
                        "ml-auto size-4",
                        value === country.code ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// PhoneInput Component
interface PhoneInputProps {
  value?: string;
  /** Called with E.164 formatted number (e.g., "+14155551234") */
  onChange?: (formattedValue: string) => void;
  defaultCountry?: CountryCode;
  disabled?: boolean;
  className?: string;
  glassEffect?: boolean;
  /** Size of OTP input slots. Use "xs" for mobile-optimized compact layout. */
  slotSize?: "default" | "sm" | "xs";
}

function PhoneInput({
  value = "",
  onChange,
  defaultCountry = "US",
  disabled = false,
  className,
  glassEffect = false,
  slotSize = "sm",
}: PhoneInputProps) {
  const [country, setCountry] = React.useState<CountryCode>(defaultCountry);
  const format = getPhoneFormat(country);
  const totalDigits = getTotalDigits(format);

  // Extract just the local phone digits from the value (strip country calling code)
  const extractLocalDigits = (
    val: string,
    countryCode: CountryCode
  ): string => {
    if (!val) return "";
    const callingCode = String(getCountryCallingCode(countryCode));
    // Remove all non-digits
    const allDigits = val.replace(/\D/g, "");
    // If the digits start with the calling code, strip it
    if (allDigits.startsWith(callingCode)) {
      return allDigits.slice(callingCode.length).slice(0, totalDigits);
    }
    // Otherwise just take the digits (up to max length)
    return allDigits.slice(0, totalDigits);
  };

  const digitsOnly = extractLocalDigits(value, country);

  // Format to E.164
  const formatE164 = (digits: string, countryCode: CountryCode): string => {
    if (!digits) return "";
    const callingCode = getCountryCallingCode(countryCode);
    return `+${callingCode}${digits}`;
  };

  // Build the OTP groups based on the format
  const renderGroups = () => {
    const groups: React.ReactNode[] = [];
    let slotIndex = 0;

    format.forEach((groupSize, groupIndex) => {
      const slots: React.ReactNode[] = [];

      for (let i = 0; i < groupSize; i++) {
        slots.push(
          <InputOTPSlot
            glassEffect={glassEffect}
            index={slotIndex}
            key={slotIndex}
            size={slotSize}
          />
        );
        slotIndex++;
      }

      groups.push(
        <InputOTPGroup key={`group-${groupIndex}`}>{slots}</InputOTPGroup>
      );

      // Add separator between groups (not after last group)
      // Commented out - using gap instead
      // if (groupIndex < format.length - 1) {
      //   groups.push(
      //     <InputOTPSeparator
      //       className="text-muted-foreground"
      //       key={`sep-${groupIndex}`}
      //     />
      //   );
      // }
    });

    return groups;
  };

  const handleDigitsChange = (digits: string) => {
    onChange?.(formatE164(digits, country));
  };

  const handleCountryChange = (newCountry: CountryCode) => {
    setCountry(newCountry);
    // Re-format with new country code if we have digits
    if (digitsOnly) {
      onChange?.(formatE164(digitsOnly, newCountry));
    } else {
      onChange?.("");
    }
  };

  return (
    <div className={cn("flex items-center gap-1.5 justify-center", className)}>
      <CountrySelect
        disabled={disabled}
        onChange={handleCountryChange}
        size={slotSize === "xs" ? 16 : 28}
        value={country}
      />
      <InputOTP
        containerClassName={cn(slotSize === "xs" ? "gap-2" : "gap-2")}
        disabled={disabled}
        inputMode="numeric"
        maxLength={totalDigits}
        onChange={handleDigitsChange}
        pattern="[0-9]*"
        value={digitsOnly}
      >
        {renderGroups()}
      </InputOTP>
    </div>
  );
}

export {
  // Original OTP exports
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
  // Phone input exports
  PhoneInput,
  CountrySelect,
  // Utility exports
  getPhoneFormat,
  getTotalDigits,
  isValidPhoneNumber,
  SUPPORTED_COUNTRIES,
  // Types
  type CountryData,
  type PhoneInputProps,
  type CountrySelectProps,
};
